import {PageSettings} from '../../model/page-settings.model';
import * as VehicleMaintenanceActions from './vehicle-maintenance.actions';
import {MaintenanceScope} from '../../model/maintenance-scope';

/**
 * The interface defining the redux sub-store
 */
export interface State {
  vehicles: VehicleBaseDto[];
  vehicle: VehicleBaseDto;
  vehiclesBasket: VehicleBaseDto[];
  settings: ViewTag[];
  searchFilterConfig: SearchFilterConfig,
  activeCategory: Section,
  selectedFilters: SearchFilter[],
  pageSettings: PageSettings,
  openTasks: OpenTaskListDto[];
  maintenanceScope: MaintenanceScope,
  userSubstitutions: UserSubstituteInformationDto[];
}

// default values - will be overridden at demand
const defaultPageSettings = new PageSettings();
defaultPageSettings.channel = 'ALL_UC';
defaultPageSettings.size = 25; // if changed please see for offering.component, too
defaultPageSettings.page = 0;
defaultPageSettings.sort = 'CREATED';
defaultPageSettings.order = 'ASC';

/**
 * The reducer acts on actions as defined below, and is the only way to update state
 * The JS spread operator for objects ('...') is used to update a given property of our state
 * while leaving the remaining properties of the state unchanged
 *
 * @param {State} state: the initial state of the redux sub-store
 * @param {VehicleMaintenanceAction} action: the actions that this sub-store is managing
 * @returns {any} updated state of the redux sub-store
 */
export function vehicleReducer(state: State = {
  vehicle: undefined,
  vehicles: [],
  vehiclesBasket: [],
  settings: [],
  pageSettings: defaultPageSettings,
  searchFilterConfig: undefined,
  activeCategory: undefined,
  selectedFilters: [],
  openTasks: [],
  maintenanceScope: undefined,
  userSubstitutions: []
},
action: VehicleMaintenanceActions.VehicleMaintenanceAction) {
  switch (action.type) {
  case VehicleMaintenanceActions.UPDATED_VEHICLE:
    return {
      ...state,
      vehicle: action.payload // update our stored vehicleDtos with the action's payload
    };
  case VehicleMaintenanceActions.UPDATED_VEHICLE_IN_LIST:
    return updateVehicleInVehicleList(state, action.payload);
  case VehicleMaintenanceActions.RESET_VEHICLE:
    return resetVehicle(state); // reset vehicle-store at page leave
  case VehicleMaintenanceActions.UPDATED_MAINTENANCE_VEHICLES:
    return {
      ...state,
      vehicles: action.payload
    };
  case VehicleMaintenanceActions.UPDATE_VIEW_SETTINGS:
    return {
      ...state,
      settings: action.settings // update our stored viewSettings with the action's payload
    };
  case VehicleMaintenanceActions.UPDATED_ENFORCED_AUCTION_VEHICLE:
    return updateVehicleInVehicleList(state, action.payload);
  case VehicleMaintenanceActions.UPDATE_VEHICLES_BASKET:
    return insertVehicleToBasket(state, action.vehicle); // update our stored basket with the action's payload
  case VehicleMaintenanceActions.DELETE_VEHICLE_FROM_BASKET:
    return deleteVehicleFromBasket(state, action.vehicle); // update our stored basket with the action's payload
  case VehicleMaintenanceActions.RESET_VEHICLE_BASKET:
    return resetVehicleBasket(state); // delete our stored basket with the action's payload*/
  case VehicleMaintenanceActions.UPDATED_VEHICLE_MAINTENANCE_SEARCH_CONFIG:
    return {
      ...state,
      searchFilterConfig: action.filterConfig
    };
  case VehicleMaintenanceActions.UPDATED_VEHICLE_MAINTENANCE_SEARCH_ACTIVE_CATEGORY:
    return {
      ...state,
      activeCategory: action.payload,
      selectedFilters: []
    };
  case VehicleMaintenanceActions.UPDATED_VEHICLE_MAINTENANCE_SEARCH_SELECTED_FILTERS:
    return {
      ...state,
      selectedFilters: action.filters
    };
  case VehicleMaintenanceActions.UPDATED_VEHICLE_MAINTENANCE_SEARCH_PAGE_SETTINGS:
    return {
      ...state,
      pageSettings: action.pageSettings
    };
  case VehicleMaintenanceActions.UPDATED_OPEN_TASKS:
    return {
      ...state,
      openTasks: action.openTasks
    };
  case VehicleMaintenanceActions.UPDATE_SCOPE:
    return {
      ...state,
      maintenanceScope: action.payload
    };
  case VehicleMaintenanceActions.UPDATED_USER_SUBSTITUTIONS:
    return {
      ...state,
      userSubstitutions: action.payload
    };
  case VehicleMaintenanceActions.UPDATE_PAGE_SETTINGS_PAGE_CHANNEL:
    return {
      ...state,
      pageSettings: {
        ...state.pageSettings,
        page: action.page,
        channel: action.channel
      }
    };
  default:
    return state;
  }
}

/**
 * This method actually removes the bookmarked item from the forced auction vehicles list and returns the updated list.
 *
 * @param state
 * @param payload
 */
function updateVehicleInVehicleList(state: State, payload: EnforcedAuctionVehicleBaseDto | VehicleBaseDto): State {
  const index = state.vehicles['content'].findIndex(findVehicle => payload.id === findVehicle.id);

  const newVehicles = structuredClone(state.vehicles);
  newVehicles['content'].splice(index, 1, payload);

  return {
    ...state,
    vehicles: {
      ...state.vehicles,
      ...newVehicles
    }
  };
}

function insertVehicleToBasket(state: State, vehicle: VehicleBaseDto): State {
  state.vehiclesBasket.push(vehicle);
  return {...state, vehiclesBasket: [...state.vehiclesBasket]};
}

function deleteVehicleFromBasket(state: State, vehicle: VehicleBaseDto): State {
  const index = state.vehiclesBasket.findIndex(findVehicle => vehicle.id === findVehicle.id);
  if (index > -1) {
    state.vehiclesBasket.splice(index, 1);
  }
  return {...state, vehiclesBasket: [...state.vehiclesBasket]};
}

function resetVehicleBasket(state: State): State {
  state.vehiclesBasket = [];
  return {...state, vehiclesBasket: [...state.vehiclesBasket]};
}

function resetVehicle(state: State): State {
  state.vehicle = null;
  return {...state, vehicle: null};
}
